import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  CircularProgress,
  Divider,
  InputAdornment,
  Grid,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import residentService from "../services/residentService";
import { useTenant } from "../contexts/TenantContext";
import { ResidentDTO } from "../types/resident.types";
import DailyProgressNoteForm from "../components/DailyProgressNoteForm";
import MedicationAdministrationRecord from "../components/MedicationAdministrationRecord";

const ProgressNotesPage: React.FC = () => {
  const { tenant } = useTenant();
  const [residents, setResidents] = useState<ResidentDTO[]>([]);
  const [selectedResident, setSelectedResident] = useState<ResidentDTO | null>(null);
  const [residentsLoading, setResidentsLoading] = useState(false);

  const fetchResidents = async () => {
    if (!tenant?.id) return;
    setResidentsLoading(true);
    try {
      const response = await residentService.getAllResidents(0, 2000, tenant.id);
      setResidents(response.content || []);
    } catch (err) {
      console.error("Failed to fetch residents", err);
    } finally {
      setResidentsLoading(false);
    }
  };

  useEffect(() => {
    fetchResidents();
  }, [tenant]);

  const [searchInput, setSearchInput] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Sort residents alphabetically by name
  const sortedResidents = useMemo(() => {
    return [...residents].sort((a: any, b: any) => {
      const nameA = (a.residentName || `${a.firstName || ""} ${a.lastName || ""}`).toLowerCase().trim();
      const nameB = (b.residentName || `${b.firstName || ""} ${b.lastName || ""}`).toLowerCase().trim();
      return nameA.localeCompare(nameB);
    });
  }, [residents]);

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };


  return (
    <Box sx={{ width: "100%", maxWidth: "100%", px: 2, pb: 2, pt: 3, }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Daily Medication
      </Typography>

      {/* ── Controls Bar ── */}
      <Box sx={{
        bgcolor: "#FFFFFF",
        borderRadius: "16px",
        border: "1px solid #E2E8F0",
        p: 2,
        mb: 3,
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap"
      }}>
        <Autocomplete
          id="resident-selector"
          options={sortedResidents.filter(r => r?.status?.toUpperCase() === "ACTIVE")}
          getOptionLabel={(option) =>
            `${(option as any).residentName || `${option.firstName} ${option.lastName}`} ${option.roomNumber ? `(Room: ${option.roomNumber}${option.bedNumber ? `, Bed: ${option.bedNumber}` : ""})` : ""}`
          }
          loading={residentsLoading}
          value={selectedResident}
          onChange={(event: any, newValue: any | null) => {
            setSelectedResident(newValue);
          }}
          inputValue={searchInput}
          onInputChange={(event, newInputValue) => {
            setSearchInput(newInputValue);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search Resident..."
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <React.Fragment>
                    {residentsLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#f5f7f8",
                },
              }}
            />
          )}
          sx={{ flex: 1, minWidth: 250 }}
        />
      </Box>

      {selectedResident ? (
        <Grid container spacing={2} sx={{ mt: 1 }} alignItems="stretch">
          {/* Left Column: Form */}
          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <DailyProgressNoteForm
              residentId={selectedResident.id || null}
              onSuccess={handleRefresh}
              sx={{ height: '100%', width: '100%' }}
            />
          </Grid>

          {/* Right Column: MAR Chart */}
          <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
            <Box sx={{ width: "100%", height: '100%' }}>
              <MedicationAdministrationRecord
                residentId={selectedResident.id!}
                refreshTrigger={refreshTrigger}
                showResidentName={false}
              />
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Paper sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
          <Typography>Please select a resident to view the form.</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ProgressNotesPage;
